// Helpers tipados sobre Firebase Analytics (GA4).
//
// Catálogo cerrado de eventos: añadir nuevos requiere ampliar el tipo
// `AppEvent`, así evitamos eventos sin nombre canónico flotando por la app.
// Los nombres siguen snake_case (convención GA4) y arrancan con un verbo
// que indica acción del usuario o del sistema.
//
// Privacidad: nunca se envían datos identificables (PII): ni email del
// usuario, ni números de contenedor, ni comentarios libres. Solo IDs
// internos numéricos y métricas agregadas.
import { logEvent, setUserId as faSetUserId, setUserProperties } from 'firebase/analytics';
import { getFirebaseAnalytics } from './firebase';

// Eventos canónicos. Cada entrada documenta el contexto y los parámetros.
type AppEvent =
  | { name: 'login_attempt'; params?: { method: 'password' } }
  | { name: 'login_success'; params?: Record<string, never> }
  | { name: 'login_failure'; params: { reason: 'wrong_credentials' | 'network' | 'server' | 'unknown' } }
  | { name: 'position_open'; params: { id_cargo: number } }
  | { name: 'detail_create_attempt'; params: { online: boolean; photo_count: number } }
  | { name: 'detail_create_success'; params: { online: boolean } }
  | { name: 'detail_create_offline_queued'; params: { photo_count: number } }
  | { name: 'detail_edit_success'; params: { online: boolean } }
  | { name: 'detail_delete_success'; params: { online: boolean } }
  | { name: 'photo_uploaded'; params: { size_kb: number } }
  | { name: 'photo_upload_failed'; params: { size_kb: number; status_code: number | string } }
  | { name: 'offline_queue_synced'; params: { synced_count: number; failed_count: number; duration_ms: number } }
  | { name: 'quick_filter_used'; params: { dim: 'controller' | 'factory' | 'carrier' | 'pending'; selected: number } }
  | { name: 'coverage_changed'; params: { level: 'fast' | 'normal' | 'slow' | 'connecting' | 'manual_offline' | 'offline' } }
  | { name: 'ocr_used'; params: { field_type: string; result: 'accepted' | 'rejected' | 'failed' } }
  | { name: 'vgm_sent'; params: { success: boolean; batch: boolean } }
  | { name: 'app_update_prompt_shown'; params?: Record<string, never> }
  | { name: 'app_update_applied'; params?: Record<string, never> }
  | { name: 'app_update_loop_detected'; params: { attempts: number } }
  // El guardado en primer plano agotó su presupuesto de tiempo antes de subir
  // todas las fotos: el resto se encoló y la cola las subirá sola.
  | { name: 'submit_budget_queued'; params: { photos_left: number } }
  | { name: 'submit_photo_failure_queued'; params: { photos_left: number } }
  // El watchdog del guardado tuvo que cortar el submit entero (cuelgue fuera
  // de los puntos con presupuesto propio) y encolarlo todo.
  | { name: 'submit_hard_deadline'; params: { photo_count: number; poor_connection: boolean } };

// Track de un evento. Sin op si Analytics no está disponible (ej. dev sin
// Firebase configurado, o en navegadores que no soportan Analytics).
export function track<E extends AppEvent>(event: E): void {
  const fa = getFirebaseAnalytics();
  if (!fa) return;
  try {
    logEvent(fa, event.name, (event as { params?: Record<string, unknown> }).params ?? {});
  } catch { /* silencioso — telemetría nunca debe romper la app */ }
}

// User ID — usar valor estable y NO PII. Pasamos un hash corto del
// username; en GA4 se usa para el conteo de usuarios únicos sin
// almacenar el email real.
export async function setAnalyticsUser(username: string | null): Promise<void> {
  const fa = getFirebaseAnalytics();
  if (!fa) return;
  try {
    if (username) {
      const id = await hashShort(username);
      faSetUserId(fa, id);
    } else {
      faSetUserId(fa, null);
    }
  } catch { /* sin op */ }
}

export function setAnalyticsUserProperties(props: {
  app_version?: string;
  pwa_installed?: boolean;
  platform?: string;
}): void {
  const fa = getFirebaseAnalytics();
  if (!fa) return;
  try {
    setUserProperties(fa, props);
  } catch { /* sin op */ }
}

// SHA-256 truncado a 16 hex chars. Suficiente para distinguir usuarios sin
// poder reconstruir el email original.
async function hashShort(input: string): Promise<string> {
  try {
    const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input.toLowerCase()));
    return Array.from(new Uint8Array(buf))
      .slice(0, 8)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    // Fallback muy básico si no hay SubtleCrypto (entornos antiguos).
    let h = 0;
    for (let i = 0; i < input.length; i++) {
      h = ((h << 5) - h + input.charCodeAt(i)) | 0;
    }
    return `fallback_${Math.abs(h).toString(16)}`;
  }
}
